import InternalLink from 'components/UI/InternalLink'
import Svg, { SvgProps } from 'components/UI/SVG'
import ArticlesArrow from 'images/articles-arrow.svg'
import { ReactElement, ReactNode } from 'react'
import { Flex, Text, ThemeUIStyleObject, get } from 'theme-ui'
import getArticleSlug from 'utils/getArticleSlug'
import getEnglishStrings from 'utils/getEnglishStrings'
import usePlatformTagId from 'utils/usePlatformTagId'
import categoriesIconMap from './categoriesIconMap'

export const categoriesYOffset = 2.4

export default function Categories({
  articles,
  categories,
  categoryViewAllText,
}: {
  articles: Queries.KnowledgeBaseIndexPageQuery['directus']['knowledge_base_articles']
  categories: Queries.KnowledgeBaseIndexPageQuery['directus']['knowledge_base_categories']
  categoryViewAllText: string | undefined | null
}): ReactElement {
  const gapX = '4.8rem'
  const gapY = '4.8rem'

  const popularArticles = articles?.filter(article =>
    article?.tags?.find(tag => tag?.knowledge_base_tags_id?.slug === 'popular'),
  )

  return (
    <Flex
      data-testid="categories"
      sx={{
        flexDirection: 'column',
        maxWidth: '100%',
        width: ['100%', '100%', '138rem'],
        height: ['100%', '100%', '100%'],
        alignItems: 'center',
        borderRadius: ['0rem', '3.2rem', '1.6rem'],
        padding: ['0', '0 4.8rem 0 4.8rem', '0'],
        mt: '4rem',
      }}
    >
      <Flex
        sx={{
          flexDirection: ['column', 'row', 'row'],
          width: ['100%', '100%', 'maxContentWidth'],
          alignItems: 'center',
          justifyContent: 'center',
          top: `-7.4rem`,
          gap: ['1.6rem', '4rem'],
          mx: ['1.2rem', '2.4rem'],
          flexWrap: ['nowrap', 'wrap', 'wrap'],
          // eslint-disable-next-line
          '& > *': {
            flex: [
              '0 1 100%',
              `1 0 calc(50% - calc(${gapX} / 2))`,
              `1 0 calc(50% - calc(${gapX} / 2))`,
            ],
          },
        }}
      >
        {categories?.map(category => {
          const categoryStrings = getEnglishStrings(category?.strings)
          const categoryArticles = articles?.filter(
            article => article?.category?.id === category?.id,
          )

          if (!categoryArticles.length) {
            return null
          }

          return (
            <Category
              name={categoryStrings?.name}
              articles={categoryArticles}
              articlesType="categories"
              slug={category?.slug}
              categoryViewAllText={categoryViewAllText}
              key={category?.slug}
            />
          )
        })}
        {/* Pseudo-category that is actually just articles tagged with 'popular' tag */}
        {!!popularArticles.length && (
          <Category
            name="Popular Articles"
            articles={popularArticles}
            articlesType="tags"
            slug="popular"
            key="popular"
            containerSx={{
              maxWidth: ['100%', '100%', `calc(100% - ${categoryMargin / 2}rem)`],
            }}
            categoryViewAllText={categoryViewAllText}
          />
        )}
      </Flex>
    </Flex>
  )
}

export const categoryMargin = 1.6

function Category({
  name,
  articles,
  articlesType,
  slug,
  categoryViewAllText,
  containerSx,
}: {
  name?: string
  articles: Queries.KnowledgeBaseIndexPageQuery['directus']['knowledge_base_articles']
  articlesType: string
  slug?: string
  categoryViewAllText?: string
  containerSx?: ThemeUIStyleObject
}) {
  const { selectedPlatformTagId } = usePlatformTagId()
  return (
    <Flex
      data-testid={`category-${slug}`}
      sx={{
        ...containerSx,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <KBWrapper>
        <Flex
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            py: '2.4rem',
          }}
        >
          <Svg
            svg={categoriesIconMap[name || '']}
            sx={{ m: '0rem 0.8rem 0 1.6rem', flexShrink: 0 }}
          />
          <Text data-testid="category-name" as="h3" variant="h3Big">
            {name}
          </Text>
        </Flex>
        <Flex
          sx={{
            gap: '0.8rem',
            flexDirection: 'column',
            width: '100%',
            marginBottom: '1rem',
            pl: '2.4rem',
            cursor: 'default',
          }}
        >
          {articles?.slice(0, 4).map(article => {
            const articleStrings = getEnglishStrings(article?.strings)
            const articleSlug = getArticleSlug(
              article as Queries.DirectusData_knowledge_base_articles,
            )
            return (
              <Flex
                data-testid={`article-${articleSlug}`}
                key={article?.id}
                sx={{
                  flexDirection: 'column',
                  width: '100%',

                  '&:last-of-type .divider': {
                    display: 'none',
                  },
                  gap: '0.8rem',
                }}
              >
                <InternalLink
                  href={
                    selectedPlatformTagId
                      ? `/knowledge-base/articles/${articleSlug}?platform=${selectedPlatformTagId}`
                      : `/knowledge-base/articles/${articleSlug}`
                  }
                  sx={{
                    width: '100%',
                  }}
                >
                  <Flex
                    data-testid="article-link"
                    sx={{
                      textDecoration: 'none',
                      width: '100%',
                      maxWidth: '100%',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      display: 'flex',
                      ':hover': {
                        '.articles-arrow path': {
                          strokeOpacity: 1,
                        },
                        span: {
                          color: 'white',
                        },
                      },
                    }}
                  >
                    <Text
                      data-testid="article-title"
                      sx={{
                        display: 'inline-block',
                        whiteSpace: ['unset', 'nowrap'],
                        overflowX: 'hidden',
                        wordBreak: ['break-word', 'break-word', 'unset'],
                        textOverflow: 'ellipsis',
                      }}
                      variant="paragraphSmallRegular"
                    >
                      {articleStrings?.title}
                    </Text>
                    <ArticlesArrowComponent sxx={{ mr: '1.6rem' }} className="articles-arrow" />
                  </Flex>
                </InternalLink>
                <div
                  className="divider"
                  sx={{
                    width: '100%',
                    height: '0.2rem',
                    backgroundColor: 'white10',
                  }}
                />
              </Flex>
            )
          })}
        </Flex>
        <Flex sx={{ width: '100%' }}>
          <Flex
            sx={{
              height: '0.2rem',
              width: '100%',
              backgroundColor: 'white10',
            }}
          ></Flex>
        </Flex>
        <Flex
          sx={{
            width: '100%',
            height: '100%',
            pl: '2.4rem',
          }}
        >
          <InternalLink
            href={
              selectedPlatformTagId
                ? `/knowledge-base/${articlesType}/${slug}?platform=${selectedPlatformTagId}`
                : `/knowledge-base/${articlesType}/${slug}`
            }
            sx={{
              width: '100%',
              ':hover': {
                '.articles-arrow path': {
                  strokeOpacity: 1,
                },
                span: {
                  color: 'white',
                },
              },
            }}
          >
            <Flex
              sx={{
                width: '100%',
                alignItems: 'center',
                my: '1.6rem',
                '.articles-arrow path': {
                  mr: '1.6rem',
                },
              }}
            >
              <Flex
                data-testid="category-view-all"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Text variant="paragraphSmallBold">{categoryViewAllText}</Text>
                <Flex sx={{ alignItems: 'center' }}>
                  <Text sx={{ marginRight: '1rem' }}>{articles?.length}</Text>
                  <ArticlesArrowComponent sxx={{ mr: '1.6rem' }} className="articles-arrow" />
                </Flex>
              </Flex>
            </Flex>
          </InternalLink>
        </Flex>
      </KBWrapper>
    </Flex>
  )
}

export function KBWrapper({ children }: { children: ReactNode }): ReactElement {
  return (
    <Flex
      sx={{
        width: '100%',
        maxWidth: '98rem',
        mx: ['1.6rem', 0],
        flexDirection: 'column',
        border: '0.2rem solid',
        borderColor: 'white10',
        position: 'relative',
        borderRadius: '1.6rem',
        alignItems: 'center',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          zIndex: -1,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: ({ colors }) =>
            `linear-gradient(0deg, ${colors?.transparent}, ${colors?.white10} 100%)`,
          opacity: 0.5,
          transition: theme => `${get(theme, 'transitions.default')}`,
        },
        ':hover': {
          '&::before': {
            opacity: 1,
          },
        },
        backgroundClip: 'padding-box',
      }}
    >
      {children}
    </Flex>
  )
}

interface ArticlesArrowComponentProps extends Omit<SvgProps, 'svg'> {
  dataTestId?: string
  sxx?: ThemeUIStyleObject
}

export function ArticlesArrowComponent(props: ArticlesArrowComponentProps): ReactElement {
  const { sxx, dataTestId, ...rest } = props
  return (
    <Svg
      svg={ArticlesArrow}
      sx={{
        flexShrink: 0,
        width: '1.6rem',
        height: '1.6rem',
        path: {
          transition: theme => `${get(theme, 'transitions.default')}`,
          strokeOpacity: 0.25,
        },
        ...sxx,
      }}
      data-testid={dataTestId}
      {...rest}
    />
  )
}
